import { useFragment } from '@apollo/client';
import { SetStateAction, useMemo } from 'react';
import { SidePanelTabs } from 'src/components';
import { RecentConversations } from 'src/components/SidePanelTabs/RecentConversations/recent-conversations';
import { gql } from 'src/gql';
import { Client, useGroupRules } from 'src/graphql';
import { ClientActivityView } from 'src/pages/Clients/client-activity-view/client-activity-view';
import { ClientSites } from 'src/pages/Clients/SidePanel/client-sites';
import useClientViewsStore from 'src/store/client-views';
import { CLIENT_TABS, ClientTabs } from './client-const';

interface ClientRelatedTabsProps {
  clientId: string;
  variant?: 'standard' | 'scrollable' | 'fullWidth';
}

interface TabContentProps {
  client: Client;
  activeTab: ClientTabs;
}

const TabContent = ({ activeTab, client }: TabContentProps) => {
  if (activeTab === ClientTabs.Sites) {
    return <ClientSites client={client} />;
  }

  if (activeTab === ClientTabs.Conversations) {
    return <RecentConversations client={client} />;
  }

  if (activeTab === ClientTabs.Activity) {
    return <ClientActivityView clientId={client.clientId} partnerId={client.partnerId} />;
  }

  return null;
};

const sidePanelInnerSx = { overflow: 'unset' };

const ClientRelatedTabsFragment = gql(`
  fragment ClientRelatedTabs_Client on Client {
    id
    clientId
    partnerId
    firstName
    lastName
    fullName @client
    deletedAt
    primaryEmail {
      id
      value
    }
  }
`);

export const ClientRelatedTabs = ({
  clientId,
  variant = 'standard',
}: ClientRelatedTabsProps) => {
  const { selectedClientTab, setSelectedClientTab } = useClientViewsStore();
  const { checkValidity, loading } = useGroupRules();

  const { data: client, complete } = useFragment({
    fragment: ClientRelatedTabsFragment,
    fragmentName: 'ClientRelatedTabs_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });

  // TODO(gql-migrate): ClientSites and RecentConversations still consume the legacy mapped
  // Client shape; drop this bridge once they read the cache via useFragment themselves.
  const legacyClient = useMemo(
    () =>
      ({
        id: client.id,
        clientId,
        partnerId: client.partnerId,
        firstName: client.firstName,
        lastName: client.lastName,
        fullName: client.fullName,
        primaryEmail: client.primaryEmail?.value,
        deletedAt: client.deletedAt,
      }) as Client,
    [client, clientId],
  );

  const tabs = useMemo(
    () =>
      CLIENT_TABS.filter(
        (tab) =>
          !tab.allowedRules ||
          (!loading && checkValidity(tab.allowedRules, { matchType: 'any' })),
      ),
    [checkValidity, loading],
  );

  if (!complete) {
    return null;
  }

  return (
    <SidePanelTabs
      key={clientId}
      activeTab={selectedClientTab}
      setActiveTab={(newValue: SetStateAction<ClientTabs>) => {
        setSelectedClientTab(newValue as ClientTabs);
      }}
      tabs={tabs}
      variant={variant}
      isNested
      innerSx={sidePanelInnerSx}
    >
      <TabContent activeTab={selectedClientTab} client={legacyClient} />
    </SidePanelTabs>
  );
};
